import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as selfieSegmentationModule from "@mediapipe/selfie_segmentation";
import ShareSection from "../AddFeeds/SnapUp/components/ShareSection";
import ActionSection from "./component/ActionSection";
import PreviewAction from "./component/PreviewAction";
import { isOverResizeHandle, isOverRotationHandle, isOverSticker, PHOTOBOT_VERSION, ROTATION_HANDLE_OFFSET } from "./utils";
import { MediaBaseUrl } from "../../../../../actions/api";
import { getDefaultLogo } from "../../../../../utils";
import { FontAwesomeIcon } from "../../../../CommonComponents/AssetsHelper";
import { CUSTOM_LANUAGE, UPDATE_PHOTOBOOTH } from "../../../../../actions/appsMoreAction";
import PreviewSection from "../AddFeeds/SnapUp/components/PreviewSection";
import ImagePreview from "./component/ImagePreview";
import './photobooth.scss';
import { ASSETS_URL } from "../../../../../actions/constant";

const CANVAS_WIDTH = 304;
const CANVAS_HEIGHT = 304;
const AVATAR_IMG = `${MediaBaseUrl}images/photobooth/ai-avatar.jpg`;

const placeholders = {
    title: 'Share you Experience',
    subTitle: 'Upload your post to get featured on Social Wall.',
    btnText: 'Submit Post',
    thankUMsg: 'Thanks for the submission',
}

const PhotoBooth = () => {
    // Refs for canvas elements, and the HTML text overlay
    const canvasRef = useRef(null);
    const deleteButtonRefs = useRef({});

    //form setting
    const [state, setState] = useState({
        settingAction: "",
        title: "",
        subTitle: "",
        btnText: "",
        thankUMsg: "",
        logoImg: "",
        language: "",
        approvePublic: "",
        isloading: false
    });
    const [loading, setLoading] = useState(false);

    const [show, setShow] = useState(false);
    useEffect(() => {
        if(show) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }, [show]);

    // State variables for application logic
    const [capturedImage, setCapturedImage] = useState(null); // image after cropping
    const [segmentationMask, setSegmentationMask] = useState(null);
    const [frameOverlay, setFrameOverlay] = useState(null);
    const [bgImg, setBgImg] = useState(null); // Stores the loaded background image
    const [stickers, setStickers] = useState([]); // Array of all stickers on the canvas
    const [selectedBg, setSelectedBg] = useState(null);
    const [selectedFrame, setSelectedFrame] = useState(null); 


    const [activeStickerIndex, setActiveStickerIndex] = useState(null); // Index of the sticker being interacted with
    const [dragMode, setDragMode] = useState(null); // 'move', 'resize', 'rotate', or null
    const [dragStart, setDragStart] = useState(null);
    const [canvasCursor, setCanvasCursor] = useState('default');

    // States for saving and download
    const [canvasDims, setCanvasDims] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });

    const dispatch = useDispatch();

    const { wallData, walls, userData } = useSelector(state => {
        return {
            wallData: state.wallData?.wallData,
            walls: state.analyticwallD.userfeeds,
            userData: state.authenticated.userData
        }
    }, shallowEqual);
    const photoBoothData = wallData?.wall?.PhotoBoothSettings ?? {};

    const handleParentChange = (key, value) => {
        if(["appliedBackground", "appliedFrame", "appliedSticker"].includes(key)) {
            if(key == "appliedBackground") setSelectedBg(value);
            else if(key == "appliedFrame") setSelectedFrame(value);
            else addSticker(value);
        } else setState(prev => ({ ...prev, [key]: value }));
        setActiveStickerIndex(null);
    };
    const onTextFieldChange = (e) => setState(prev => ({ ...prev, [e.target.name]: e.target.value, [`${e.target.name}Error`]: false }));

    const submitSetting = () => {
        const { title, subTitle, btnText, thankUMsg, logoImg, onsiteEnable, approvePublic, language, settingAction } = state;
        let obj = { wallId: localStorage.getItem('wallId'), network: 30, filter: 10 };
        if (settingAction == 2) {
            if (title && subTitle && btnText && thankUMsg) {
                obj = { ...obj,
                    photo_booth_popup_title: title,
                    photo_booth_popup_sub_heading: subTitle,
                    photo_booth_logo: logoImg && !['tagbox.svg', 'tagembed.svg'].some(item => logoImg.includes(item)) ? logoImg : `${MediaBaseUrl}dashboard/tagbox-logo-dark.png`,
                    photo_booth_popup_btn_txt: btnText,
                    photo_booth_succ_msg: thankUMsg,
                    photo_booth_status: onsiteEnable,
                    photo_booth_moderation: approvePublic,
                    photo_booth_language: language
                }
            } else {
                const keysToCheck = ['title', 'subTitle', 'btnText', 'thankUMsg'];
                keysToCheck.forEach(key => {
                    if (!state[key]) setState(prev => ({ ...prev, [`${key}Error`]: true }));
                })
                return;
            }
        } else {
            obj = {
                ...obj,
                photo_booth_status: Number(onsiteEnable),
                photo_booth_moderation: approvePublic,
            }
        }

        setState(prev => ({ ...prev, isloading: true }));
        dispatch(UPDATE_PHOTOBOOTH(obj, () => { setState(prev => ({ ...prev, isloading: false })) }))
    }

    // Function to generate selfie segmentation mask using MediaPipe
    const generateSegmentation = async (image) => {
        const selfieSegmentation = new selfieSegmentationModule.SelfieSegmentation({
            locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
        });
        selfieSegmentation.setOptions({ modelSelection: 1 });
        selfieSegmentation.onResults((results) => {
            setSegmentationMask(results.segmentationMask);
        });

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = image.width;
        tempCanvas.height = image.height;
        const tempCtx = tempCanvas.getContext("2d");
        tempCtx.drawImage(image, 0, 0, image.width, image.height);

        try {
            await selfieSegmentation.send({ image: tempCanvas });
        } catch (error) {
            console.error("Error sending image to MediaPipe:", error);
        }
    }

    // Helper function to get mouse coordinates relative to the canvas's drawing dimensions
    const getCanvasCoordsFromEvent = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        // Calculate coordinates considering the canvas's displayed size vs. its actual drawing size
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);
        return { x, y };
    };

    // Function to capture a photo from the webcam
    const takePhoto = async () => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = `${ASSETS_URL}${AVATAR_IMG}?v=${PHOTOBOT_VERSION}`
        img.onload = async () => {
            setCapturedImage(img);
            setCanvasDims({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }); // Ensure canvas dims are set on photo capture
            await generateSegmentation(img);
        };
        img.onerror = () => {
            console.error("Failed to load captured image.");
        };
    }

    const onMouseDown = (e) => {
        const { x: mouseX, y: mouseY } = getCanvasCoordsFromEvent(e, canvasRef);
        let foundSticker = false;

        // === CHECK STICKERS ===
        for (let i = stickers.length - 1; i >= 0; i--) {
            const sticker = stickers[i];

            const deleteBtn = deleteButtonRefs.current[i];
            if (deleteBtn) {
                const { x, y, size } = deleteBtn;
                const dx = mouseX - x;
                const dy = mouseY - y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < size / 2) {
                    setStickers((prev) => prev.filter((_, idx) => idx !== i));
                    setActiveStickerIndex(null);
                    return;
                }
            }

            if (isOverRotationHandle(sticker, mouseX, mouseY)) {
                setActiveStickerIndex(i);
                setDragMode("rotate");
                setDragStart({
                    x: mouseX,
                    y: mouseY,
                    centerX: sticker.x + (sticker.img.width * sticker.scale) / 2,
                    centerY: sticker.y + (sticker.img.height * sticker.scale) / 2,
                    initialAngle: sticker.rotation || 0,
                });
                foundSticker = true;
                break;
            }

            if (isOverResizeHandle(sticker, mouseX, mouseY)) {
                setActiveStickerIndex(i);
                setDragMode("resize");
                setDragStart({
                    x: mouseX,
                    y: mouseY,
                    initialScale: sticker.scale,
                    initialWidth: sticker.img.width,
                });
                foundSticker = true;
                break;
            }

            if (isOverSticker(sticker, mouseX, mouseY)) {
                setActiveStickerIndex(i);
                setDragMode("move");
                setDragStart({
                    x: mouseX,
                    y: mouseY,
                    initialX: sticker.x,
                    initialY: sticker.y,
                });
                foundSticker = true;
                break;
            }
        }

        // Clear selection if clicked outside everything
        if (!foundSticker) {
            setDragMode(null);
            setDragStart(null);
            setActiveStickerIndex(null);
        }
    };    

    const onMouseMove = (e) => {
        const { x: mouseX, y: mouseY } = getCanvasCoordsFromEvent(e);
    
        let newCursor = 'default';
        let foundHover = false;
    
        // 🔍 If not hovering text, check stickers
        if (!foundHover) {
            for (let i = stickers.length - 1; i >= 0; i--) {
                const sticker = stickers[i];
                if (isOverRotationHandle(sticker, mouseX, mouseY)) {
                    newCursor = 'crosshair';
                    foundHover = true;
                    break;
                } else if (isOverResizeHandle(sticker, mouseX, mouseY)) {
                    newCursor = 'nwse-resize';
                    foundHover = true;
                    break;
                } else if (isOverSticker(sticker, mouseX, mouseY)) {
                    newCursor = 'grab';
                    foundHover = true;
                    break;
                }
            }
        }
    
        // Show grabbing cursor if dragging
        if (dragMode === 'move' && (activeStickerIndex !== null)) {
            newCursor = 'grabbing';
        }
    
        setCanvasCursor(newCursor);
    
        // 💥 Abort if not dragging
        if (!dragMode || !dragStart) return;
    
        const dx = mouseX - dragStart.x;
        const dy = mouseY - dragStart.y;
    
        // ✏️ Dragging sticker
        if (activeStickerIndex !== null) {
            setStickers((prev) => {
                const updated = [...prev];
                const item = { ...updated[activeStickerIndex] };
    
                if (dragMode === 'move') {
                    item.x = dragStart.initialX + dx;
                    item.y = dragStart.initialY + dy;
                } else if (dragMode === 'resize') {
                    const newWidth = dragStart.initialWidth * dragStart.initialScale + dx;
                    const newScale = Math.max(0.05, newWidth / item.img.width);
                    item.scale = newScale;
                } else if (dragMode === 'rotate') {
                    const angleStart = Math.atan2(dragStart.y - dragStart.centerY, dragStart.x - dragStart.centerX);
                    const angleCurrent = Math.atan2(mouseY - dragStart.centerY, mouseX - dragStart.centerX);
                    const angleDelta = angleCurrent - angleStart;
                    const degrees = (angleDelta * 180) / Math.PI;
                    item.rotation = (dragStart.initialAngle + degrees) % 360;
                }
    
                updated[activeStickerIndex] = item;
                return updated;
            });
        }
    };

    // Event handler for mouse up on the canvas (ends drag/resize)
    const onMouseUp = () => {
        setDragMode(null);
        setDragStart(null);
        setCanvasCursor('default'); // Reset cursor
    }

    // Function to add a new sticker to the canvas
    const addSticker = (src) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = `${ASSETS_URL}${src}?v=${PHOTOBOT_VERSION}`
        setLoading(true);
        img.onload = () => {
            setLoading(false);
            // Use canvasDims for initial positioning and scaling
            const width = canvasDims.width;
            const height = canvasDims.height;

            const initialWidth = 100; // Default width for a new sticker
            // Calculate initial height to maintain aspect ratio
            const initialHeight = (img.height / img.width) * initialWidth;
            const initialScale = initialWidth / img.width; // Scale factor to achieve initialWidth

            setStickers((prev) => {
                const newStickers = [
                    ...prev,
                    {
                        img,
                        url: src,
                        x: width / 2 - initialWidth / 2, // Centered horizontally
                        y: height / 2 - initialHeight / 2, // Centered vertically
                        scale: initialScale,
                        rotation: 0, // Initialize rotation to 0 degrees
                    },
                ];
                // Set the newly added sticker as active
                setActiveStickerIndex(newStickers.length - 1);
                return newStickers;
            });
        };
        img.onerror = () => {
            setLoading(false);
            console.error(`Failed to load sticker image: ${src}`);
        };
    };

    // useEffect hook to draw on the canvas whenever relevant states change
    useEffect(() => {
        // Only draw if an image and segmentation mask are available
        if (!capturedImage || !segmentationMask) return;

        const canvas = canvasRef.current;
        if (!canvas) return; // Ensure canvas element exists
        const ctx = canvas.getContext("2d");

        // Use canvasDims from state for consistent dimensions
        const width = canvasDims.width;
        const height = canvasDims.height;

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Create an offscreen buffer canvas for drawing to prevent flickering
        const bufferCanvas = document.createElement("canvas");
        bufferCanvas.width = width;
        bufferCanvas.height = height;
        const bufferCtx = bufferCanvas.getContext("2d");

        // Clear the buffer canvas
        bufferCtx.clearRect(0, 0, width, height);

        // --- Logic for Background Replacement ---
        if (selectedBg && bgImg) {
            // If a background is selected, perform segmentation and composite
            const foregroundCanvas = document.createElement('canvas');
            foregroundCanvas.width = width;
            foregroundCanvas.height = height;
            const fgCtx = foregroundCanvas.getContext('2d');

            fgCtx.drawImage(capturedImage, 0, 0, width, height);
            fgCtx.globalCompositeOperation = 'destination-in';
            fgCtx.drawImage(segmentationMask, 0, 0, width, height);
            fgCtx.globalCompositeOperation = 'source-over';

            bufferCtx.drawImage(bgImg, 0, 0, width, height);
            bufferCtx.drawImage(foregroundCanvas, 0, 0, width, height);
        } else {
            // If NO background is selected, draw the ORIGINAL captured image
            bufferCtx.drawImage(capturedImage, 0, 0, width, height);
        }

        // Ensure globalCompositeOperation is 'source-over' for subsequent elements
        bufferCtx.globalCompositeOperation = "source-over";

        // Apply frame overlay if available - it's static and fills the canvas
        if (frameOverlay) {
            bufferCtx.drawImage(frameOverlay, 0, 0, width, height);
        }

        // Draw all active stickers with their positions, scales, and rotations
        stickers.forEach((sticker, index) => {
            const stickerWidth = sticker.img.width * sticker.scale;
            const stickerHeight = sticker.img.height * sticker.scale;
            const centerX = sticker.x + stickerWidth / 2;
            const centerY = sticker.y + stickerHeight / 2;

            bufferCtx.save(); // Save the current canvas state

            // Translate to the sticker's center, rotate, then translate back
            bufferCtx.translate(centerX, centerY);
            bufferCtx.rotate((sticker.rotation || 0) * Math.PI / 180); // Convert degrees to radians
            bufferCtx.drawImage(sticker.img, -stickerWidth / 2, -stickerHeight / 2, stickerWidth, stickerHeight);

            // Draw border and handles if this is the active sticker
            if (index === activeStickerIndex) {
                bufferCtx.strokeStyle = "#2b2b2b"; // Blue border
                bufferCtx.lineWidth = 2;
                bufferCtx.setLineDash([5, 3]); // [dashLength, gapLength]
                bufferCtx.strokeRect(-stickerWidth / 2, -stickerHeight / 2, stickerWidth, stickerHeight);

                // Draw Resize Handle (bottom-right)
                const icon = "⤡";
                bufferCtx.fillStyle = "black"; // Outer circle
                bufferCtx.beginPath();
                bufferCtx.arc(stickerWidth / 2, stickerHeight / 2, 12, 0, 2 * Math.PI);
                bufferCtx.fill();

                // Draw ✕ icon inside the circle
                bufferCtx.fillStyle = "white";
                bufferCtx.font = "20px sans-serif";

                // Measure to center the icon inside the circle
                const textMetrics = bufferCtx.measureText(icon);
                const iconX = stickerWidth / 2 - textMetrics.width / 2;
                const iconY = stickerHeight / 2 + 7; // ~4 to adjust vertical centering
                bufferCtx.fillText(icon, iconX, iconY);

                // Draw Rotation Handle (top-center, above sticker)
                const rotationHandleX = stickerWidth / 2 + 3.5;  // Right edge from center
                const rotationHandleY = -stickerHeight / 2 - ROTATION_HANDLE_OFFSET + 22;
                
                bufferCtx.beginPath();
                bufferCtx.fillStyle = "black"; // Fill color for handle
                bufferCtx.arc(rotationHandleX, rotationHandleY, 12, 0, 2 * Math.PI);
                bufferCtx.fill();
                bufferCtx.fillStyle = "white";
                bufferCtx.font = "20px sans-serif";
                const iconXx = (stickerWidth / 2 - textMetrics.width / 2) + 3;
                const iconYy = (-stickerHeight / 2) + 7; // ~4 to adjust vertical centering
                bufferCtx.fillText('⟳', iconXx, iconYy);

                // ✅ Draw Delete Button (top-right corner)
                const deleteBtnSize = 16;
                const deleteBtnX = -stickerWidth / 2 - deleteBtnSize / 2 + 8;  // Shift left of center
                const deleteBtnY = -stickerHeight / 2 - deleteBtnSize / 2 + 8; 
                deleteButtonRefs.current[index] = {
                    x: centerX + deleteBtnX * Math.cos((sticker.rotation * Math.PI) / 180) - deleteBtnY * Math.sin((sticker.rotation * Math.PI) / 180),
                    y: centerY + deleteBtnX * Math.sin((sticker.rotation * Math.PI) / 180) + deleteBtnY * Math.cos((sticker.rotation * Math.PI) / 180),
                    size: deleteBtnSize,
                };
                // Drawing the button
                bufferCtx.beginPath();
                bufferCtx.arc(deleteBtnX, deleteBtnY, 12, 0, 2 * Math.PI);
                bufferCtx.fillStyle = "black";
                bufferCtx.fill();
                bufferCtx.fillStyle = "white";
                bufferCtx.font = "14px sans-serif";
                const iconXd = (-stickerWidth / 2) - 5;  // Moved from right to left
                const iconYd = (-stickerHeight / 2) + 5; 
                bufferCtx.fillText('✕', iconXd, iconYd);
            }
            bufferCtx.restore(); // Restore the canvas state
        });

        // Draw the buffered content to the main visible canvas
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(bufferCanvas, 0, 0);

        // The saving logic has been moved to handleSave, so removed from here.
    }, [capturedImage, segmentationMask, frameOverlay, stickers, activeStickerIndex, bgImg, canvasDims, state.settingAction]);

    // useEffect hook to load the selected background image
    useEffect(() => {
        if (!selectedBg) {
            setBgImg(null); // Clear background if none selected
            return;
        }
        setLoading(true);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = `${ASSETS_URL}${selectedBg}?v=${PHOTOBOT_VERSION}`;
        img.onload = () => {
            setLoading(false);
            setBgImg(img);
        };
        img.onerror = () => {
            setLoading(false);
            console.error(`Failed to load background image: ${selectedBg}`);
        };
    }, [selectedBg]);

    // useEffect hook to load the selected frame image (static)
    useEffect(() => {
        if (!selectedFrame) {
            setFrameOverlay(null); // Clear frame if none selected
            return;
        }
        setLoading(true);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = `${ASSETS_URL}${selectedFrame}?v=${PHOTOBOT_VERSION}`;
        setLoading(true);
        img.onload = () => {
            setLoading(false);
            setFrameOverlay(img)
        };
        img.onerror = () => {
            setLoading(false);
            console.error(`Failed to load frame image: ${selectedFrame}`);
        };
    }, [selectedFrame]);

    useEffect(() => {
        takePhoto();
    }, [])

    useEffect(() => {
        if(photoBoothData?.photo_booth_popup_title && !state.title){
            const getLang = async () => {
                try {
                    await CUSTOM_LANUAGE((data) => setState(prev => ({ ...prev, allLang: data })));
                } catch (error) { console.log(error) }
            }
            getLang();
            const savedLogo = photoBoothData?.photo_booth_logo;
            const logoImage = getDefaultLogo(savedLogo);
            setState(prev => ({
                ...prev,
                title: photoBoothData?.photo_booth_popup_title,
                subTitle: photoBoothData?.photo_booth_popup_sub_heading,
                btnText: photoBoothData?.photo_booth_popup_btn_txt,
                thankUMsg: photoBoothData?.photo_booth_succ_msg,
                onsiteEnable: Number(photoBoothData?.photo_booth_status),
                logoImg: logoImage,
                logoImgUploaded: !!savedLogo && !['/tagbox', 'tagembed', 'social-wall'].some(sub => savedLogo.includes(sub)),
                onsite_language: photoBoothData?.photo_booth_language,
                approvePublic: photoBoothData?.photo_booth_moderation,
                language: photoBoothData?.photo_booth_language,
            }))
        }
    }, [photoBoothData])

    return (
        <div className="p-3 photo-booth">
            {
                wallData ? 
                <div className="d-flex flex-wrap">
                    <div className={`photobooth-left ${show ? 'active' : ''}`}>
                        <ActionSection {...{ selectedBg, selectedFrame, setSelectedFrame, stickers, addSticker, takePhoto, photoBoothData, handleParentChange, onTextFieldChange, submitSetting }} formState={state} />
                        <Button variant="light" className="pb-feature-button" onClick={() => setShow(!show)}>
                            <FontAwesomeIcon type="regular" icon="gear" />
                        </Button>
                    </div>
                    <div className="photobooth-right">
                  
                        <div className="ra-presha bg-white rounded-8 w-100 shadow py-xxl-0 py-4 px-3">
                            <div className="d-flex flex-wrap align-items-center h-100 w-100">
                                <div className="col-xxl-6 col-12 h-100 me-xxl-5 pb-height-auto">
                                    {state.settingAction == 2 ? <PreviewSection {...state} placeholders={placeholders} IMGCOMP={<ImagePreview {...{ canvasRef, canvasCursor }} />} /> : <PreviewAction {...{ language:state?.language, canvasCursor, canvasRef, onMouseDown, onMouseMove, onMouseUp, loading, settingAction: state.settingAction }} />}
                                </div>
                                <div className="col-xxl-5 col-12">
                                    <ShareSection wallsData={walls} wallDetails={wallData} userData={userData} status={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div> : null
            }
        </div>
    );
};

export default PhotoBooth;